import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import DataTableV3 from 'src/components/DataTableV3';
import DocumentTitle from 'src/components/DocumentTitle/DocumentTitle';
import { DataTableV3Ids } from 'src/constants/dataTableIds';
import useDocumentTitle from 'src/hooks/use-document-title';
import useOneWayInServices from '../../one-way-in/hooks/use-one-way-in-services';
import {
  useAllServicesTableColumns,
  useMyServicesInactiveTableColumns,
  useServiceTableColumns,
} from '../hooks/use-service-table-columns';
import { OneWayInServiceDto } from 'src/api/v2';
import QueryParamMultiSelect from 'src/components/Filters/QueryParamMultiSelect';
import ResetFilterButtonOld from 'src/components/ResetFilterButton/ResetFilterButtonOld';
import { useServiceCounties } from '../../one-way-in/opportunity-details/hooks/use-counties';
import { useClusters } from 'src/features/cluster';
import ServiceInfo from '../../one-way-in/opportunity-details/components/referral/ServiceInfo';
import ServicesTable from '../components/ServicesTable';
import { Counties } from 'src/features/one-way-in/constants/counties';
import { SERVICES } from '../routes';
import useGeonorge from 'src/features/one-way-in/hooks/use-geonorge';
import { ServiceDeliveryBarChart } from 'src/features/home/components/front-page/OwiStatistics';
import useOwiStatistics from 'src/features/home/hooks/use-owi-statistics';
import { Button, Card, Heading, Skeleton, Tabs } from '@innovationnorway/cl-react';
import useServiceTemplatesList from 'src/features/one-way-in/hooks/use-service-templates';
import '../services.scss';
import ServiceTemplateSelector from '../components/ServiceTemplateSelector.tsx/ServiceTemplateSelector';
import { useLocalStorage } from 'src/hooks/use-local-storage';
import { useCluster } from 'src/features/cluster';
import { filterWithNormalization } from 'src/features/one-way-in/opportunity-details/utils/searchQueryHelper';
import { FilterFn } from '@tanstack/react-table';

enum TabNames {
  ClusterServices = 'clusterServices',
  AllServices = 'allServices',
}

enum QueryParams {
  County = 'county',
  ActiveTab = 'activeTab',
  SearchQuery = 'q',
}

const Services = () => {
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tCommon } = useTranslation('common');
  const { t: tServices } = useTranslation('services');
  const { documentTitle } = useDocumentTitle(tOneWayIn('tabs.Services'));
  const { cluster } = useCluster();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = useMemo(() => {
    return searchParams.get(QueryParams.ActiveTab) || TabNames.ClusterServices;
  }, [searchParams]);
  const {
    activeClusterServices,
    inactiveClusterServices,
    draftClusterServices,
    allActiveServices,
    isLoading,
  } = useOneWayInServices(selectedTab === TabNames.ClusterServices && cluster ? cluster.id : undefined);
  const activeColumns = useServiceTableColumns('active');
  const draftColumns = useMyServicesInactiveTableColumns('draft');
  const inactiveColumns = useMyServicesInactiveTableColumns('inactive');
  const allServicesColumns = useAllServicesTableColumns();
  const counties = useServiceCounties(allActiveServices);
  const { clusters } = useClusters();
  const { getMunicipalityNumberByName } = useGeonorge();
  const stats = useOwiStatistics();
  const [hasOpenedServiceTemplateSupport, setHasOpenedServiceTemplateSupport] = useLocalStorage(
    'hasOpenedServiceTemplateSupport',
    false,
  );
  useEffect(() => {
    if (hasOpenedServiceTemplateSupport) return;
    setHasOpenedServiceTemplateSupport(true);
    window.dispatchEvent(
      new CustomEvent('openSideMenu', {
        detail: 'index/commonQuestions/owi/serviceTemplates',
      }),
    );
  }, [hasOpenedServiceTemplateSupport, setHasOpenedServiceTemplateSupport]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const { allServiceTemplates } = useServiceTemplatesList();

  const firstTemplate = useMemo(() => {
    return allServiceTemplates ? allServiceTemplates[0] : undefined;
  }, [allServiceTemplates]);

  const { selectedCounties } = useMemo(() => {
    return {
      selectedCounties: searchParams.get(QueryParams.County)?.split(',') ?? [],
    };
  }, [searchParams]);

  const filterByCounty = (item: OneWayInServiceDto): boolean => {
    if (selectedCounties.length === 0) return true;
    if (
      selectedCounties.includes(tOneWayIn('nationWide')) &&
      item.clusterId === '00000000-0000-0000-0000-000000000000' &&
      item.municipalities.length === 0
    )
      return true;

    if (item.municipalities.length === 0) {
      const county = clusters.find((cluster) => cluster.id === item.clusterId)?.address?.county;
      if (!county) return selectedCounties.includes('');
      return selectedCounties.includes(county);
    }

    const counties: string[] = [];
    for (const municipality of item.municipalities) {
      const municipalityNumber = isNaN(Number(municipality))
        ? getMunicipalityNumberByName(municipality)
        : municipality;
      const county = Counties.find((county) =>
        county.municipalities.some((municipality) => municipality === municipalityNumber),
      );
      if (county) {
        counties.push(county.countyName);
      }
    }

    return [...new Set(counties)].some((county) => selectedCounties.includes(county));
  };

  const filteredServices: OneWayInServiceDto[] = allActiveServices.filter(filterByCounty) || [];

  const normalizedGlobalFilterFn: FilterFn<OneWayInServiceDto> = (row, columnId, filterValue) => {
    const cellValue = row.getValue<string | number | null | undefined>(columnId);
    if (typeof cellValue === 'string') {
      return filterWithNormalization(cellValue, filterValue);
    }
    if (typeof cellValue === 'number') {
      return cellValue.toString().includes(filterValue);
    }
    return false;
  };

  function handleClearFilter() {
    if (searchParams.get(QueryParams.ActiveTab) === TabNames.AllServices)
      setSearchParams(`${QueryParams.ActiveTab}=${TabNames.AllServices}`, { replace: true });
    else setSearchParams(undefined, { replace: true });
  }
  const filters = (
    <div className="display--flex flex-wrap--wrap align-items--center gap--2 margin-0-on-all-children">
      <QueryParamMultiSelect
        options={counties.counties}
        queryStringParam={QueryParams.County}
        name="county"
        label={tCommon('county')}
        placeHolder={tCommon('select')}
      />

      <ResetFilterButtonOld className="margin-top--2" onClick={handleClearFilter} />
    </div>
  );

  return (
    <DocumentTitle title={documentTitle}>
      <main className="full-width-layout stack background-color--black-12">
        <Heading level={1} className="margin-bottom--0">
          {tServices('serviceCatalog')}
        </Heading>
        <div className="display--flex flex-direction--column gap--2">
          <Button
            asChild
            onClick={() => {
              if (allServiceTemplates !== undefined && allServiceTemplates?.length > 0) {
                setDialogOpen(true);
              }
            }}
          >
            <Link
              className="color--white"
              to={
                allServiceTemplates === undefined || allServiceTemplates?.length === 0
                  ? SERVICES.NEW_SERVICE
                  : `${SERVICES.BASE}/serviceTemplate/${firstTemplate?.id}`
              }
            >
              {tServices('addService')}
            </Link>
          </Button>
        </div>
        <Tabs value={selectedTab}>
          <Tabs.List>
            <Tabs.Tab
              value={TabNames.ClusterServices}
              onClick={() => {
                setSearchParams(`${QueryParams.ActiveTab}=${TabNames.ClusterServices}`, { replace: true });
              }}
              className="background-color--black-12"
            >
              {tServices('clusterServices')}
            </Tabs.Tab>
            <Tabs.Tab
              value={TabNames.AllServices}
              onClick={() => {
                setSearchParams(`${QueryParams.ActiveTab}=${TabNames.AllServices}`, { replace: true });
              }}
              className="background-color--black-12"
            >
              {tServices('allServices')}
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel className="background-color--black-12" value={TabNames.ClusterServices}>
            <div className=" service-cluster-layout display--flex flex-direction--row gap--3 align-items--flex-start box-sizing--border-box">
              <div className="service-cluster-layout__main display--flex flex-direction--column width--70 gap--3">
                <ServicesTable
                  data={activeClusterServices}
                  columns={activeColumns}
                  title={tServices('activeServices')}
                  isLoading={isLoading}
                  tableId={DataTableV3Ids.Services.ClusterServicesActive}
                />
                <ServicesTable
                  data={draftClusterServices}
                  columns={draftColumns}
                  title={tCommon('draft')}
                  isLoading={isLoading}
                  tableId={DataTableV3Ids.Services.ClusterServicesDraft}
                />
                <ServicesTable
                  data={inactiveClusterServices}
                  columns={inactiveColumns}
                  title={tServices('inactiveServices')}
                  isLoading={isLoading}
                  tableId={DataTableV3Ids.Services.ClusterServicesInactive}
                />
              </div>
              <div className="service-cluster-layout__aside display--flex flex-direction--column gap--3">
                <Card className="border--none">
                  {!stats.data ? (
                    <Skeleton />
                  ) : (
                    <ServiceDeliveryBarChart data={stats.data?.serviceDeliveryMap} />
                  )}
                </Card>
              </div>
            </div>
          </Tabs.Panel>
          <Tabs.Panel className="background-color--black-12" value={TabNames.AllServices}>
            <div className="box borderless background-color--white">
              <DataTableV3
                id={DataTableV3Ids.Services.AllServices}
                disableSelection={true}
                loading={isLoading}
                data={filteredServices}
                customColumnFilters={filters}
                columns={allServicesColumns}
                disableColumnEditor
                disableExport
                hideFilterText
                globalFilterFn={normalizedGlobalFilterFn}
                getRowCanExpand={() => true}
                initialState={{ globalFilter: searchParams.get(QueryParams.SearchQuery) ?? '' }}
                onGlobalFilterChange={(e) => {
                  const value = e.target.value;
                  setSearchParams(
                    (prev) => {
                      prev.set(QueryParams.SearchQuery, value);
                      return prev;
                    },
                    { replace: true },
                  );
                }}
                renderSubComponent={({ row }) => <ServiceInfo row={row} />}
                texts={{
                  globalFilterPlaceholder: tOneWayIn('serviceSearchInputPlaceHolder'),
                }}
                pageSize={25}
              />
            </div>
          </Tabs.Panel>
        </Tabs>
        <ServiceTemplateSelector
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          allServiceTemplates={allServiceTemplates}
        />
      </main>
    </DocumentTitle>
  );
};

export default Services;
